<template>
    <transition name="slide">
        <div
            v-if="show"
            class="notification-root p-3 shadow"
            :class="{ 'error-root': state === errorState() }"
            @click="deleteImmediately"
        >
            <audio v-if="playSound" :src="notificationSound" type="audio/x-m4a" autoplay />
            <div class="d-flex align-items-center mb-2">
                <BaseIcon
                    v-if="icon || errorIcon"
                    class="icon-color"
                    :name="icon || errorIcon"
                    size="lg"
                    :class="{ 'error-color': error === true }"
                />
                <h4 class="subtitle bold mb-0">
                    <strong>{{ title }}</strong>
                </h4>
            </div>

            <div>
                <h6 class="subtitle">{{ message }}</h6>
            </div>

            <div v-if="linkHref">
                <router-link class="link" :to="linkHref">{{ linkText }}</router-link>
            </div>
        </div>
    </transition>
</template>

<script>
import { MessageState } from '~/store/customModules/messageModule';
import NotificationSound from '~/assets/audio/notification.mp3';

export default {
    name: 'Notification',
    props: {
        id: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        message: {
            type: String,
            default: '',
        },
        state: {
            type: String,
            default: null,
        }, // MessageState,
        duration: {
            type: Number,
            default: null,
        },
        persistent: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            default: null,
        },
        linkHref: {
            type: String,
            default: null,
        },
        linkText: {
            type: String,
            default: null,
        },
        playSound: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        show: false,
        timeout: null,
        errorIcon: null,
        error: false,

        notificationSound: NotificationSound,
    }),
    methods: {
        deleteMessage() {
            this.show = false;
            setTimeout(() => {
                this.$interfaces.messageModule.deleteMessage(this.id);
            }, 1000);
            this.$emit('close');
        },
        deleteMessageAfterDuration() {
            if (this.persistent) return;
            this.timeout = setTimeout(
                this.deleteMessage,
                this.duration || 5000 // Default duration at 5000
            );
        },
        deleteImmediately() {
            clearTimeout(this.timeout);
            this.deleteMessage();
        },
        setStateValues() {
            switch (this.state) {
                case MessageState.ERROR:
                    this.errorIcon = 'exclamation-triangle';
                    this.error = true;
                    break;
                case MessageState.NOTIFICATION:
                    this.errorIcon = 'envelope';
                    break;
                case MessageState.WEBINAR:
                    this.errorIcon = 'play-circle';
                    break;
            }
        },
        errorState() {
            return MessageState.ERROR;
        },
        startAnimation() {
            this.show = false;
            this.$nextTick(() => {
                this.show = true;
            });
        },
    },
    mounted() {
        this.deleteMessageAfterDuration();
        this.setStateValues();
        this.startAnimation();
    },
};
</script>

<style scoped>
.notification-root {
    background-color: white;
    border: 2px solid var(--secondary);
    box-sizing: border-box;

    cursor: pointer;
    z-index: 10000;
}
.notification-root:hover {
    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
    transform: scale(1.01);
}

.error-root {
    border: 2px solid var(--error);
}

/* STATE COLORS */
.icon-color {
    color: rgba(0, 0, 0, 0.7);
}
.error {
    background-color: var(--error);
    border: none;
}
.error-color {
    color: var(--error);
}
.white-color {
    color: white !important;
}

/* ANIMATIONS */
.slide-enter-active {
    transition: all 0.5s;
}
.slide-leave-active {
    transition: all 0.6s ease;
}
.slide-enter /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
    transform: translateY(40px);
}
.slide-leave-to {
    transform: translateY(40px);
    opacity: 0;
}

@media only screen and (max-width: 600px) {
    .notification-root {
        width: 100%;
    }
}
</style>
